import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/DateInput";
import { Label } from "@/components/ui/label";
import { AppShell } from "@/components/AppShell";
import { api, formatApiErrorDetail } from "@/api";
import { toast } from "sonner";
import { Download, Search } from "lucide-react";
import { toDMY } from "@/lib/dates";

const L = ({ children }) => (
  <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--mod-primary)]">{children}</Label>
);

const rs = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

const TABS = [
  { key: "", label: "Line wise", testId: "bale-tab-lines" },
  { key: "bale", label: "Bale wise", testId: "bale-tab-bale" },
  { key: "lr", label: "LR wise", testId: "bale-tab-lr" },
];

export default function BaleReport() {
  const [profile, setProfile] = useState(null);
  const [search, setSearch] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [group, setGroup] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (groupKey = group) => {
    setLoading(true);
    try {
      const { data } = await api.get("/bale-report", {
        params: { search, date_from: from, date_to: to, group: groupKey },
      });
      setData(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const download = async (format) => {
    try {
      const res = await api.get("/bale-report/export", {
        params: { search, date_from: from, date_to: to, group, format },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const base = group === "bale" ? "bale-wise-report" : group === "lr" ? "lr-wise-report" : "bale-and-lr-report";
      a.href = url;
      a.download = `${base}.${format === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not export this report");
    }
  };

  useEffect(() => {
    api.get("/profile").then(({ data }) => setProfile(data)).catch(() => {});
    load("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const grouped = !!data?.group;
  const groupLabel = data?.group === "bale" ? "Bale no." : "LR no.";
  const otherLabel = data?.group === "bale" ? "LR nos." : "Bale nos.";

  return (
    <AppShell profile={profile}>
      <main className="mx-auto max-w-[1500px] px-4 py-8 sm:px-8">
        <div className="mod-band rounded-sm px-5 py-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--mod-primary)]">Display</p>
          <h1 className="font-head mt-2 text-4xl font-extrabold tracking-tighter sm:text-5xl">Bale &amp; LR Report</h1>
          <p className="mt-2 text-sm text-zinc-500">
            Purchase lines that carry a bale number or an LR number — flat, bale wise or LR wise.
          </p>
        </div>

        <div className="mt-6 flex flex-wrap gap-1 border-b border-zinc-200">
          {TABS.map((t) => (
            <button
              key={t.label}
              data-testid={t.testId}
              onClick={() => {
                setGroup(t.key);
                load(t.key);
              }}
              className={`border-b-2 px-4 py-2 text-xs font-bold uppercase tracking-[0.15em] transition-colors ${
                group === t.key ? "border-[#C8A03C] text-zinc-950" : "border-transparent text-zinc-500 hover:text-zinc-950"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-4 py-6 sm:grid-cols-5 sm:items-end">
          <div className="sm:col-span-2">
            <L>Search bale / LR / item / supplier</L>
            <div className="relative mt-2">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input
                data-testid="bale-search-input"
                className="rounded-sm pl-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                placeholder="e.g. B-12 or LR-9987"
              />
            </div>
          </div>
          <div>
            <L>From</L>
            <DateInput data-testid="bale-from-input" className="mt-2 rounded-sm" inputClassName="h-9 w-full rounded-sm border border-zinc-200 bg-white px-3 text-sm outline-none focus:border-[var(--mod-primary)]" value={from} onChange={(v) => setFrom(v)} />
          </div>
          <div>
            <L>To</L>
            <DateInput data-testid="bale-to-input" className="mt-2 rounded-sm" inputClassName="h-9 w-full rounded-sm border border-zinc-200 bg-white px-3 text-sm outline-none focus:border-[var(--mod-primary)]" value={to} onChange={(v) => setTo(v)} />
          </div>
          <div className="flex gap-2">
            <Button data-testid="bale-run-button" className="rounded-sm bg-[var(--mod-primary)] text-white hover:opacity-90" onClick={() => load()}>
              Show
            </Button>
            <Button data-testid="bale-export-pdf" variant="outline" className="rounded-sm" onClick={() => download("pdf")}>
              <Download size={14} className="mr-1.5" /> PDF
            </Button>
            <Button data-testid="bale-export-excel" variant="outline" className="rounded-sm" onClick={() => download("excel")}>
              Excel
            </Button>
          </div>
        </div>

        {loading ? (
          <p className="py-16 text-center text-sm text-zinc-500">Loading…</p>
        ) : !data || data.rows.length === 0 ? (
          <div data-testid="bale-empty-state" className="border border-dashed border-zinc-300 p-12 text-center">
            <h3 className="font-head text-xl font-bold tracking-tight">Nothing found</h3>
            <p className="mt-1 text-sm text-zinc-500">Bale and LR numbers are entered on each purchase line.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-6" data-testid="bale-totals">
              {[
                ["Lines", data.totals.lines],
                ["Bales", data.totals.bales],
                ["LRs", data.totals.lrs],
                ["Than", data.totals.than],
                ["Meter", data.totals.meter],
                ["Value", rs(data.totals.amount)],
              ].map(([label, value]) => (
                <div key={label} className="border border-zinc-200 bg-white p-4">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">{label}</p>
                  <p className="font-mono-num mt-1.5 text-lg font-semibold">{value}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 overflow-x-auto border border-zinc-200 bg-white" data-testid="bale-table">
              {grouped ? (
                <table className="w-full text-sm">
                  <thead className="border-b border-zinc-200">
                    <tr className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400">
                      {[groupLabel, "Date", "Bill no.", "Supplier", "Items", otherLabel, "Lines", "Than", "Meter", "Value"].map((h) => (
                        <th key={h} className={`px-3 py-2 ${["Lines", "Than", "Meter", "Value"].includes(h) ? "text-right" : "text-left"}`}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    {data.rows.map((r, i) => (
                      <tr key={r.key} data-testid={`bale-group-row-${i}`} className="hover:bg-zinc-50">
                        <td className="font-mono-num px-3 py-2 font-semibold">{r.key}</td>
                        <td className="font-mono-num px-3 py-2 whitespace-nowrap">{toDMY(r.date)}</td>
                        <td className="px-3 py-2">{r.bills || "-"}</td>
                        <td className="px-3 py-2">{r.suppliers || "-"}</td>
                        <td className="px-3 py-2 uppercase">{r.items || "-"}</td>
                        <td className="font-mono-num px-3 py-2">{r.others || "-"}</td>
                        <td className="font-mono-num px-3 py-2 text-right">{r.lines}</td>
                        <td className="font-mono-num px-3 py-2 text-right">{r.than}</td>
                        <td className="font-mono-num px-3 py-2 text-right">{r.meter}</td>
                        <td className="font-mono-num px-3 py-2 text-right">{rs(r.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table className="w-full text-sm">
                  <thead className="border-b border-zinc-200">
                    <tr className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400">
                      {["Date", "Vch no.", "Bill no.", "Supplier", "Bale no.", "LR no.", "Item", "Shade", "Centre", "Than", "Meter", "Value"].map((h) => (
                        <th key={h} className={`px-3 py-2 ${["Than", "Meter", "Value"].includes(h) ? "text-right" : "text-left"}`}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    {data.rows.map((r, i) => (
                      <tr key={`${r.ref_no}-${r.bale_no}-${i}`} data-testid={`bale-row-${i}`} className="hover:bg-zinc-50">
                        <td className="font-mono-num px-3 py-2 whitespace-nowrap">{toDMY(r.date)}</td>
                        <td className="px-3 py-2 font-semibold">{r.ref_no}</td>
                        <td className="font-mono-num px-3 py-2">{r.bill_no || "-"}</td>
                        <td className="px-3 py-2">{r.supplier || "-"}</td>
                        <td className="px-3 py-2 font-mono-num font-semibold">{r.bale_no || "-"}</td>
                        <td className="px-3 py-2 font-mono-num">{r.lr_no || "-"}</td>
                        <td className="px-3 py-2 uppercase">{r.item}</td>
                        <td className="px-3 py-2 font-mono-num">{r.shade || "-"}</td>
                        <td className="px-3 py-2 text-zinc-500">{r.centre || "-"}</td>
                        <td className="font-mono-num px-3 py-2 text-right">{r.than}</td>
                        <td className="font-mono-num px-3 py-2 text-right">{r.meter}</td>
                        <td className="font-mono-num px-3 py-2 text-right">{rs(r.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </main>
    </AppShell>
  );
}
